import { useEffect, useMemo, useRef, useState, useCallback, Fragment } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, SlidersHorizontal, MapPin, Building2, X, ChevronDown, Loader2, ArrowRight, SearchX, ShieldCheck } from "lucide-react";
import CollegeImage from "@/components/CollegeImage";
import AICtaBlock from "@/components/AICtaBlock";
import { track } from "@/lib/analytics";
import { summarizeList } from "@/lib/utils";

const SORT_LABELS = {
  popularity: "Most Relevant",
  alphabetical: "A → Z",
  recent: "Recently Added",
  fee: "Fees (Low→High)",
  placement: "Placements",
};
const NAVY = "#0B2A6B";

function useDebounce(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export default function SearchExperience({ lockedFilters = {}, title, subtitle, hideStateFacet = false, initialQuery = "" }) {
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState(initialQuery);
  const dq = useDebounce(q);
  const [selected, setSelected] = useState({}); // {facetKey: [values]}
  const [sort, setSort] = useState("popularity");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileFilters, setMobileFilters] = useState(false);
  const topRef = useRef(null);
  const lastSearchSig = useRef(null);

  useEffect(() => { api.get("/filters").then((r) => setMeta(r.data)).catch(() => setMeta({ facets: [], states: [] })); }, []);

  const buildParams = useCallback(() => {
    const p = { ...lockedFilters, sort, page, limit: 12 };
    if (dq) p.q = dq;
    Object.entries(selected).forEach(([k, vals]) => { if (vals?.length) p[k] = vals.join("|"); });
    return p;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dq, selected, sort, page, JSON.stringify(lockedFilters)]);

  useEffect(() => {
    setLoading(true);
    const params = buildParams();
    api.get("/search", { params }).then((r) => {
      setData(r.data);
      const sig = JSON.stringify(params);
      if (lastSearchSig.current !== sig) {
        lastSearchSig.current = sig;
        track("search_performed", {
          query: dq || undefined,
          results_count: r.data?.total,
          sort,
          page,
          ...Object.fromEntries(Object.entries(selected).filter(([, v]) => v?.length).map(([k, v]) => [k, v.join("|")])),
          ...(lockedFilters || {}),
        });
      }
    }).finally(() => setLoading(false));
  }, [buildParams]);

  useEffect(() => { setPage(1); }, [dq, selected, sort]);

  const toggle = (key, value) => {
    const cur = selected[key] || [];
    if (!cur.includes(value)) track("filter_applied", { filter_type: key, filter_value: value });
    setSelected((s) => {
      const c = s[key] || [];
      return { ...s, [key]: c.includes(value) ? c.filter((v) => v !== value) : [...c, value] };
    });
  };
  const clearAll = () => {
    track("filter_cleared", {});
    setSelected({}); setQ("");
    if (window.location.search) window.history.replaceState({}, "", window.location.pathname);
  };

  const activeCount = Object.values(selected).reduce((a, v) => a + (v?.length || 0), 0);
  const facets = useMemo(() => {
    if (!meta) return [];
    const list = [];
    if (!hideStateFacet && meta.states?.length) {
      list.push({ key: "state", label: "State", options: meta.states.map((s) => ({ value: s.slug, label: `${s.name}`, count: s.colleges })) });
    }
    (meta.facets || []).forEach((f) => list.push({ key: f.key, label: f.label, options: f.options.map((o) => ({ value: o.value, label: o.value, count: o.count })) }));
    return list;
  }, [meta, hideStateFacet]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" ref={topRef} data-testid="search-experience">
      {(title || subtitle) && (
        <div className="mb-6">
          {title && <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0B2A6B]">{title}</h1>}
          {subtitle && <p className="mt-1 text-slate-500">{subtitle}</p>}
        </div>
      )}

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <input
          data-testid="search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by college, city, state or course…"
          className="w-full rounded-2xl border border-slate-300 bg-white py-3.5 pl-12 pr-4 text-base shadow-sm outline-none focus:border-[#0B2A6B] focus:ring-2 focus:ring-[#0B2A6B]/20"
        />
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <button data-testid="mobile-filter-toggle" onClick={() => setMobileFilters(true)} className="lg:hidden inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium">
          <SlidersHorizontal className="h-4 w-4" /> Filters {activeCount > 0 && <span className="rounded-full bg-[#0B2A6B] px-1.5 text-xs text-white">{activeCount}</span>}
        </button>
        <p className="text-sm text-slate-500" data-testid="result-count">{data ? `${data.total} colleges` : "…"}</p>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-sm text-slate-400">Sort</span>
          <select data-testid="sort-select" value={sort} onChange={(e) => { setSort(e.target.value); track("sort_changed", { sort: e.target.value }); }} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0B2A6B]">
            {Object.entries(SORT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-[260px_1fr] gap-6">
        {/* Filters sidebar */}
        <aside className={`${mobileFilters ? "fixed inset-0 z-50 bg-white p-5 overflow-auto" : "hidden"} lg:block lg:static lg:p-0`} data-testid="filter-sidebar">
          <div className="flex items-center justify-between mb-3 lg:hidden">
            <h3 className="font-bold text-slate-900">Filters</h3>
            <button onClick={() => setMobileFilters(false)}><X className="h-5 w-5" /></button>
          </div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="hidden lg:block font-display font-bold text-slate-900">Filters</h3>
            {activeCount > 0 && <button data-testid="clear-filters" onClick={clearAll} className="text-xs font-semibold text-[#0B2A6B] hover:underline">Clear all</button>}
          </div>
          <div className="space-y-3">
            {facets.map((f) => <FacetGroup key={f.key} facet={f} selected={selected[f.key] || []} onToggle={toggle} />)}
          </div>
          {mobileFilters && <button onClick={() => setMobileFilters(false)} className="mt-4 w-full rounded-lg bg-[#0B2A6B] py-2.5 text-sm font-semibold text-white lg:hidden">Show {data?.total ?? 0} colleges</button>}
        </aside>

        {/* Results */}
        <div>
          {loading ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-64 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
          ) : !data || data.results.length === 0 ? (
            <div data-testid="search-empty" className="rounded-2xl border border-dashed border-slate-300 py-16 px-6 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100"><SearchX className="h-7 w-7 text-slate-400" /></div>
              <p className="mt-4 font-display text-lg font-semibold text-slate-700">Can't find your college?</p>
              <p className="mt-1 text-sm text-slate-500">Our AI can recommend better-fit alternatives based on your marks, budget & goals.</p>
              <div className="mx-auto mt-6 max-w-lg">
                <AICtaBlock ctaId="empty_search" headline="Let AI find your best-fit colleges" sub="Free, personalized, 2 minutes." buttonText="Get AI Recommendation" />
              </div>
              {(activeCount > 0 || q) && (
                <button data-testid="empty-clear-filters" onClick={clearAll} className="mt-5 inline-flex items-center gap-2 rounded-lg border border-slate-200 px-5 py-2.5 text-sm font-semibold text-[#0B2A6B] hover:bg-slate-50">
                  <X className="h-4 w-4" /> Clear All Filters
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="results-grid">
                {data.results.map((c, i) => (
                  <Fragment key={c.collegeId}>
                    <CollegeCard c={c} />
                    {i === 5 && data.results.length > 6 && (
                      <div className="sm:col-span-2 xl:col-span-3">
                        <AICtaBlock ctaId="search_inline" variantStyle="slim" headline="Still confused? Let AI shortlist the perfect colleges for you." sub="Based on your marks, budget & preferences — free." buttonText="Start AI Counselling" badges={false} />
                      </div>
                    )}
                  </Fragment>
                ))}
              </div>
              <Pagination page={data.page} totalPages={data.totalPages} onChange={(p) => { setPage(p); track("pagination", { page: p }); topRef.current?.scrollIntoView({ behavior: "smooth" }); }} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FacetGroup({ facet, selected, onToggle }) {
  const [open, setOpen] = useState(["state", "ownership", "vertical", "course"].includes(facet.key));
  const [showAll, setShowAll] = useState(false);
  const opts = showAll ? facet.options : facet.options.slice(0, 6);
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={`facet-${facet.key}`}>
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-slate-800">
        {facet.label} <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-1.5 max-h-64 overflow-auto">
          {opts.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer hover:text-slate-900">
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => onToggle(facet.key, o.value)}
                data-testid={`facet-opt-${facet.key}-${o.value}`} className="h-4 w-4 rounded border-slate-300 text-[#0B2A6B] focus:ring-[#0B2A6B]" />
              <span className="flex-1 truncate" title={o.label}>{o.label}</span>
              <span className="text-xs text-slate-400">{o.count}</span>
            </label>
          ))}
          {facet.options.length > 6 && (
            <button onClick={() => setShowAll(!showAll)} className="text-xs font-semibold text-[#0B2A6B] hover:underline">{showAll ? "Show less" : `+${facet.options.length - 6} more`}</button>
          )}
        </div>
      )}
    </div>
  );
}

function CollegeCard({ c }) {
  const courseSummary = summarizeList(c.category || c.vertical, 1);
  return (
    <div data-testid={`college-card-${c.collegeId}`} className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(11,35,82,0.04)] ring-1 ring-transparent transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-1 hover:shadow-[0_18px_40px_-16px_rgba(11,35,82,0.28)] hover:ring-[#D4A24C]/25">
      <div className="relative h-32 overflow-hidden">
        <CollegeImage src={c.image} alt={c.name} label={c.vertical || c.category?.split(",")[0] || "Institute"} />
        {c.tier?.toLowerCase().includes("tier 1") && <span className="absolute top-2.5 left-2.5 rounded-full bg-[#D4A24C] px-2 py-0.5 text-[10px] font-bold tracking-wide text-white shadow-sm">FEATURED</span>}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-display text-[15px] font-bold text-slate-900 leading-snug line-clamp-2" title={c.name}>{c.name}</h3>
        {courseSummary && <p className="mt-1.5 truncate text-[13px] font-medium text-[#9a6b1e]" title={c.category || c.vertical}>{courseSummary}</p>}
        <p className="mt-1.5 flex items-center gap-1 text-[13px] text-slate-500"><MapPin className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{[c.city, c.state].filter(Boolean).join(", ")}</span></p>
        <div className="mt-3 flex items-center gap-2">
          {c.ownership && <span className="truncate rounded-full bg-slate-50 px-2.5 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-slate-100">{c.ownership.split("(")[0].trim()}</span>}
          <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#0B2A6B]"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Verified</span>
        </div>
        <div className="mt-5 flex items-center gap-2 border-t border-slate-100 pt-4">
          <Link to={`/college/${c.slug}`} data-testid={`view-college-${c.collegeId}`} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-semibold text-[#0B2A6B] transition-colors hover:bg-slate-50">Explore College <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></Link>
          <Link to="/ai-counseling" className="inline-flex flex-1 items-center justify-center rounded-lg bg-[#0B2A6B] px-3 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-[#081E4D]">Apply Now</Link>
        </div>
      </div>
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const pages = [];
  const add = (p) => pages.push(p);
  add(1);
  for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) add(p);
  if (totalPages > 1) add(totalPages);
  const uniq = [...new Set(pages)].sort((a, b) => a - b);
  return (
    <div className="mt-8 flex items-center justify-center gap-2" data-testid="pagination">
      <button disabled={page <= 1} onClick={() => onChange(page - 1)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:opacity-40 hover:bg-slate-50">Prev</button>
      {uniq.map((p, i) => (
        <span key={p} className="flex items-center">
          {i > 0 && p - uniq[i - 1] > 1 && <span className="px-1 text-slate-400">…</span>}
          <button onClick={() => onChange(p)} className={`min-w-[38px] rounded-lg px-3 py-2 text-sm font-medium ${p === page ? "bg-[#0B2A6B] text-white" : "border border-slate-200 hover:bg-slate-50"}`}>{p}</button>
        </span>
      ))}
      <button disabled={page >= totalPages} onClick={() => onChange(page + 1)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:opacity-40 hover:bg-slate-50">Next</button>
    </div>
  );
}
